import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';

import { getTranslationKey } from 'helpers/texting';
import { useToggle } from 'hooks/useToggle';
import { useBodyClassName } from 'hooks/useBodyClassName';
import { usePasswordPage } from './usePasswordPage';

import styled from 'styled-components';
import media from 'assets/css/media';

// Hardcoded, not `var(--font)`: this page must look identical for every gallery design.
const font = "'Noto Sans', sans-serif";

// Covers the templated body background — this page is white for every gallery design.
const Page = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1;
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: #ffffff;
  font-family: ${font};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 335px;
`;

const Title = styled.h1`
  &&& {
    position: absolute;
    top: 40px;
    left: 40px;
    right: 40px;
    margin: 0;
    font-family: ${font};
    font-size: 55px;
    font-weight: 400;
    line-height: 66px;
    color: #000000;
    text-align: left;

    @media all and (max-width: ${media.mobile}) {
      position: static;
      margin: 0 0 30px;
      font-size: 29px;
      line-height: 40px;
      text-align: center;
    }
  }
`;

const Field = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled.input`
  &&& {
    width: 100%;
    height: 47px;
    padding: 0 50px 0 20px;
    border: 1px solid #dedddd;
    border-radius: 24px;
    background: #ffffff;
    box-shadow: none;
    font-family: ${font};
    font-size: 16px;
    font-weight: 400;
    color: #000000;

    &::placeholder {
      color: #9b9a9a;
      opacity: 1;
    }

    &:focus,
    &:focus-visible {
      outline: none;
      border-color: #000000;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:hover {
      -webkit-box-shadow: 0 0 0 30px #ffffff inset !important;
      -webkit-text-fill-color: #000000;
    }
  }
`;

const EyeButton = styled.button`
  &&& {
    position: absolute;
    top: 50%;
    right: 18px;
    transform: translateY(-50%);
    padding: 0;
    border: none;
    background: none;
    line-height: 0;
    cursor: pointer;
    color: #8c8c8c;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const SubmitButton = styled.button`
  &&& {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 47px;
    margin-top: 20px;
    padding: 0 20px;
    border: none;
    border-radius: 24px;
    background: #000000;
    font-family: ${font};
    font-size: 16px;
    font-weight: 400;
    color: #ffffff;
    cursor: pointer;

    &:hover {
      color: #ffffff;
      text-decoration: none;
      opacity: 0.85;
    }
  }
`;

const Notice = styled.p`
  &&& {
    margin: 20px 0 0;
    font-family: ${font};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #e50606;
    text-align: center;
  }
`;

const CookiesText = styled.p`
  &&& {
    margin: 0;
    font-family: ${font};
    font-size: 16px;
    line-height: 27px;
    color: #000000;
    text-align: center;
  }
`;

export const NewPasswordPage: React.FC = () => {
  const {
    title,
    password,
    setPassword,
    handlePasswordCheck,
    areCookiesEnabled,
    isWrongPassword,
    lockedUntil,
    lockedUntilText
  } = usePasswordPage();
  const { toggle: isPasswordShown, handleToggle: togglePasswordShowing } = useToggle();
  useBodyClassName();

  // Only ever one notice — being locked out supersedes the wrong-password hint.
  const noticeText = lockedUntil
    ? lockedUntilText
    : (isWrongPassword && getTranslationKey('passwordWrong')) || '';

  return (
    <Page data-cy="new-password-page">
      <Form onSubmit={handlePasswordCheck} data-cy="new-password-page-form">
        <Title data-cy="new-password-page-title">{title}</Title>

        {!areCookiesEnabled ? (
          <CookiesText data-cy="new-password-page-cookies">
            {getTranslationKey('allowCookies')}
          </CookiesText>
        ) : (
          <>
            <Field>
              <PasswordInput
                id="pin-password-input"
                type={isPasswordShown ? 'text' : 'password'}
                placeholder={getTranslationKey('password')}
                value={password}
                onChange={(event) => setPassword(event.target.value.trimStart())}
                onBlur={(event) => setPassword(event.target.value.trim())}
                spellCheck="false"
                aria-label={getTranslationKey('shop.forms.galleryPassword')}
                data-cy="new-password-page-input"
              />
              <EyeButton
                type="button"
                onClick={togglePasswordShowing}
                aria-label={getTranslationKey('password')}
                data-cy="new-password-page-eye"
              >
                {isPasswordShown ? <IoEyeOutline /> : <IoEyeOffOutline />}
              </EyeButton>
            </Field>

            <SubmitButton type="submit" data-cy="new-password-page-submit">
              {getTranslationKey('shop.forms.viewGallery')}
            </SubmitButton>
          </>
        )}

        {noticeText && <Notice data-cy="new-password-page-notice">{noticeText}</Notice>}
      </Form>
    </Page>
  );
};
